// Library Methods
import { useTranslation } from "react-i18next";
import {
  createContext,
  useState,
  useMemo,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { arrayMove } from "@dnd-kit/sortable";

// Types
import { ExternalExamType } from "src/services/exams/types";
import { PatientType } from "src/services/patients/types";
import { FilterGroupType } from "src/components/containers/Patients/ClinicExams/ExternalExams/ExamInsightsModal/screens/SelectionScreen/FilterSidebar/FilterSection/FilterSection";

// Contexts
import { useClinicContext } from "../clinic/ClinicProvider";

// Hooks
import { useVFExternalExams } from "src/services/exams/hooks/external-exams/useVFExternalExams";
import { useOPDExternalExams } from "src/services/exams/hooks/external-exams/useOPDExternalExams";
import { useDeleteExternalExams } from "src/services/exams/hooks/external-exams/useDeleteExternalExams";
import { useOCTExternalExams } from "src/services/exams/hooks/external-exams/useOCTExternalExams";

// Utils
import {
  getFormattedDisplayDate,
  getLocalDate,
} from "src/utils/date/dateHelper";
import { getLanguageCode } from "src/utils/locale/localeHelper";

// Constants
import { eyeTypeEnums, OCT_TYPE, VISUAL_FIELD_TYPE } from "src/constants/enums";

export type SortByOption = "laterality" | "machineType" | "examType";
export type SortDirection = "asc" | "desc";

// Right eye first by ophthalmology convention (OD, OS, OU).
const EYE_SORT_ORDER: Record<string, number> = { OD: 0, OS: 1, OU: 2 };

const ExternalExamsContext = createContext({
  selectedPatient: {} as PatientType,
  openCompareModal: false,
  openDeleteModal: false,
  selectedExams: [] as Array<ExternalExamType>,
  vfExternalExamsData: undefined as Array<ExternalExamType> | undefined,
  opdExternalExamsData: undefined as Array<ExternalExamType> | undefined,
  octExternalExamsData: undefined as Array<ExternalExamType> | undefined,
  isLoading: false,
  hasNoExternalExams: false,
  hasExternalExams: false,
  uniqueId: "",
  formattedPatientName: "",
  languageCode: "en",
  sortOptions: [] as Array<{ id: SortByOption; label: string }>,
  effectiveSortBy: null as SortByOption | null,
  sortDirection: "desc" as SortDirection,
  sortableCards: [] as Array<ExternalExamType>,
  filteredCards: [] as Array<ExternalExamType>,
  dateGroups: [] as Array<[string, Array<ExternalExamType>]>,
  examTypeFilter: {
    items: [],
    checkedIds: new Set<string>(),
    onToggleItem: (_: string) => {},
    onToggleAll: (_: boolean) => {},
  } as FilterGroupType,
  examDateFilter: {
    items: [],
    checkedIds: new Set<string>(),
    onToggleItem: (_: string) => {},
    onToggleAll: (_: boolean) => {},
  } as FilterGroupType,
  examEyeFilter: {
    items: [],
    checkedIds: new Set<string>(),
    onToggleItem: (_: string) => {},
    onToggleAll: (_: boolean) => {},
  } as FilterGroupType,
  actions: {
    setOpenCompareModal: (_: boolean) => {},
    setOpenDeleteModal: (_: boolean) => {},
    setSelectedExams: (_: React.SetStateAction<Array<ExternalExamType>>) => {},
    deleteExternalExam: (_: Array<ExternalExamType>) => {},
    handleSortByClick: (_: SortByOption) => {},
    reorderCards: (_activeId: string, _overId: string) => {},
    resetManualOrder: () => {},
  },
});

export const useExternalExamsContext = () => {
  const context = useContext(ExternalExamsContext);
  if (!context) {
    throw new Error(
      "useExternalExamsContext must be used within ExternalExamsProvider"
    );
  }

  return context;
};

type ExternalExamsProviderType = {
  selectedPatient: PatientType;
  children: React.ReactNode;
};

export const ExternalExamsProvider = ({
  selectedPatient,
  children,
}: ExternalExamsProviderType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const [openCompareModal, setOpenCompareModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedExams, setSelectedExams] = useState<Array<ExternalExamType>>(
    []
  );

  const { data: vfExternalExamsData, isLoading: isLoadingVFExams } =
    useVFExternalExams(clinicInfo?.id, selectedPatient?.id, true);

  const { data: opdExternalExamsData, isLoading: isLoadingOPDExams } =
    useOPDExternalExams(clinicInfo?.id, selectedPatient?.id, true);

  const { data: octExternalExamsData, isLoading: isLoadingOCTExams } =
    useOCTExternalExams(clinicInfo?.id, selectedPatient?.id, true);

  const { mutate: deleteMutate, isPending: isDeletePending } =
    useDeleteExternalExams(clinicInfo?.id, selectedPatient?.id);

  const uniqueId = useMemo(
    () =>
      typeof selectedPatient?.healthCareId === "string" &&
      selectedPatient?.healthCareId?.length > 0
        ? selectedPatient?.healthCareId
        : t("Blank"),
    [selectedPatient?.healthCareId, t]
  );

  const isLoading = useMemo(
    () =>
      isLoadingOPDExams ||
      isLoadingVFExams ||
      isLoadingOCTExams ||
      isDeletePending,
    [isLoadingOPDExams, isLoadingVFExams, isLoadingOCTExams, isDeletePending]
  );

  const hasNoExternalExams = useMemo(
    () =>
      Boolean(
        !isLoading &&
          !vfExternalExamsData?.length &&
          !opdExternalExamsData?.length &&
          !octExternalExamsData?.length
      ),
    [isLoading, vfExternalExamsData, opdExternalExamsData, octExternalExamsData]
  );

  const hasExternalExams = useMemo(
    () =>
      Boolean(
        !isLoading &&
          ((vfExternalExamsData && vfExternalExamsData?.length > 0) ||
            (opdExternalExamsData && opdExternalExamsData?.length > 0) ||
            (octExternalExamsData && octExternalExamsData?.length > 0))
      ),
    [isLoading, vfExternalExamsData, opdExternalExamsData, octExternalExamsData]
  );

  const deleteExternalExam = useCallback(
    (examsToDelete: Array<ExternalExamType>) => {
      const idsToDelete = examsToDelete.map((exam) => exam.id);

      deleteMutate(idsToDelete, {
        onSuccess: () => {
          setSelectedExams([]);
          setOpenDeleteModal(false);
        },
      });
    },
    [deleteMutate]
  );

  const formattedPatientName = useMemo(
    () => `${selectedPatient?.firstName} ${selectedPatient?.lastName}`,
    [selectedPatient]
  );

  // --- Comparison-modal sort/filter/date-grouping logic --------------------

  const languageCode = useMemo(
    () => getLanguageCode(clinicInfo?.settings?.clinicDefaultLanguage),
    [clinicInfo?.settings?.clinicDefaultLanguage]
  );

  const getDateGroupKey = useCallback(
    (exam: ExternalExamType) =>
      getLocalDate(exam?.date ?? "", clinicInfo?.timezone ?? null),
    [clinicInfo?.timezone]
  );

  const getExamTypeId = useCallback((exam: ExternalExamType) => {
    if (exam.examType?.includes(VISUAL_FIELD_TYPE)) return "visual-field";
    if (exam.examType?.includes(OCT_TYPE)) return "oct";
    return "opd";
  }, []);

  const getExamTypeLabel = useCallback(
    (id: string) => {
      if (id === "visual-field") return t("Visual Field");
      if (id === "oct") return t("OCT");
      return t("OPD");
    },
    [t]
  );

  // OD/OS/OU are standard ophthalmology abbreviations -- not translated.
  const getExamEyeId = useCallback((exam: ExternalExamType) => {
    if (exam.eye === eyeTypeEnums.LEFT) return "OS";
    if (exam.eye === eyeTypeEnums.RIGHT) return "OD";
    return "OU";
  }, []);

  const [sortableCards, setSortableCards] = useState<Array<ExternalExamType>>(
    []
  );

  // Current sorting criteria
  const [sortBy, setSortBy] = useState<SortByOption | null>("laterality");

  // Each button cycles independently: 1st click on a criterion -> descending,
  // 2nd click on the SAME criterion -> ascending, 3rd click -> deselect.
  // Clicking a DIFFERENT criterion always starts that cycle over at
  // descending, regardless of where the previous criterion's cycle was.
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Purely visual: set on a manual drag so the sort buttons no longer apply
  const [hasManualOrder, setHasManualOrder] = useState(false);
  const effectiveSortBy = hasManualOrder ? null : sortBy;

  const sortOptions = useMemo(
    () => [
      { id: "laterality" as const, label: t("Eye") },
      { id: "machineType" as const, label: t("Machine") },
      { id: "examType" as const, label: t("Exam Type") },
    ],
    [t]
  );

  const handleSortByClick = useCallback(
    (option: SortByOption) => {
      setHasManualOrder(false);

      if (effectiveSortBy !== option) {
        // Switching to a new (or re-selecting after a drag) criterion
        // always starts its cycle fresh at descending.
        setSortBy(option);
        setSortDirection("desc");
        return;
      }

      if (sortDirection === "desc") {
        setSortDirection("asc");
      } else {
        setSortBy(null);
        setSortDirection("desc");
      }
    },
    [effectiveSortBy, sortDirection]
  );

  const sortByComparator = useCallback(
    (option: SortByOption | null, direction: SortDirection) =>
      (a: ExternalExamType, b: ExternalExamType) => {
        const result = (() => {
          switch (option) {
            case "machineType":
              return (a.sourcePlatform ?? "").localeCompare(
                b.sourcePlatform ?? ""
              );
            case "examType":
              return getExamTypeLabel(getExamTypeId(a)).localeCompare(
                getExamTypeLabel(getExamTypeId(b))
              );
            case "laterality":
              return (
                (EYE_SORT_ORDER[getExamEyeId(a)] ?? 0) -
                (EYE_SORT_ORDER[getExamEyeId(b)] ?? 0)
              );
            default:
              // No criterion selected -- leave relative order as-is.
              return 0;
          }
        })();

        return direction === "asc" ? -result : result;
      },
    [getExamEyeId, getExamTypeId, getExamTypeLabel]
  );

  useEffect(() => {
    if (isLoading) return;

    const allExams = [
      ...(vfExternalExamsData ?? []),
      ...(opdExternalExamsData ?? []),
      ...(octExternalExamsData ?? []),
    ];

    // `hasManualOrder` is a dependency so this effect re-fires to restore
    // the auto-sort when a sort button turns manual mode back OFF, and
    // also (unavoidably) on the reverse transition right after a drag
    // sets manual order. Re-sorting from scratch on that reverse
    // transition would immediately overwrite the reorder the user just
    // made -- but skipping the update entirely would also stop fresh
    // data (e.g. a newly toggled favorite) from ever reaching
    // `sortableCards` while manual order is active. Merge instead: keep
    // the existing manual order, but swap in each card's latest data and
    // append/drop any exams that were added/removed server-side.
    if (hasManualOrder) {
      setSortableCards((prev) => {
        const examsById = new Map(allExams.map((exam) => [exam.id, exam]));
        const stillExisting = prev
          .map((exam) => examsById.get(exam.id))
          .filter((exam): exam is ExternalExamType => Boolean(exam));
        const existingIds = new Set(stillExisting.map((exam) => exam.id));
        const newExams = allExams.filter((exam) => !existingIds.has(exam.id));
        return [...stillExisting, ...newExams];
      });
      return;
    }

    // Sort exams by date groups (exams should be displayed per day)
    const byDateGroupDesc = (a: ExternalExamType, b: ExternalExamType) =>
      getDateGroupKey(b).localeCompare(getDateGroupKey(a));

    // Favorited exams float to the top within their date group -- more
    // significant than the current sort criteria, less significant than
    // the date grouping itself.
    const byFavoriteDesc = (a: ExternalExamType, b: ExternalExamType) =>
      Number(b.isFavorite) - Number(a.isFavorite);

    setSortableCards(
      [...allExams]
        .sort(sortByComparator(sortBy, sortDirection))
        .sort(byFavoriteDesc)
        .sort(byDateGroupDesc)
    );
  }, [
    isLoading,
    vfExternalExamsData,
    opdExternalExamsData,
    octExternalExamsData,
    sortBy,
    sortDirection,
    sortByComparator,
    getDateGroupKey,
    // Included to force a re-sort
    hasManualOrder,
    // Included to force a re-render when opening the modal
    openCompareModal,
  ]);

  const reorderCards = useCallback((activeId: string, overId: string) => {
    setSortableCards((prev) => {
      const oldIndex = prev.findIndex((exam) => exam.id === activeId);
      const newIndex = prev.findIndex((exam) => exam.id === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });

    // Visual only -- deselects the sort buttons without touching `sortBy`,
    // so this doesn't trigger the sort effect or reorder anything else.
    setHasManualOrder(true);
  }, []);

  // Favoriting/unfavoriting a card should always shoot it to the top of
  // its date group, even if the user had previously dragged a manual
  // order -- so drop back into auto-sort mode instead of trying to
  // preserve the manual order through it.
  const resetManualOrder = useCallback(() => {
    setHasManualOrder(false);
  }, []);

  // Which exam-type/date/eye filter checkboxes are currently checked. Every
  // id seen for the first time gets added as checked automatically -- see
  // the reset effect further below for how this stays "everything checked"
  // whenever the modal (re)opens.
  const [checkedExamTypeIds, setCheckedExamTypeIds] = useState<Set<string>>(
    new Set()
  );
  const [checkedExamDateIds, setCheckedExamDateIds] = useState<Set<string>>(
    new Set()
  );
  const [checkedExamEyeIds, setCheckedExamEyeIds] = useState<Set<string>>(
    new Set()
  );
  const knownExamTypeIdsRef = useRef<Set<string>>(new Set());
  const knownExamDateIdsRef = useRef<Set<string>>(new Set());
  const knownExamEyeIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const newIds = sortableCards
      .map(getExamTypeId)
      .filter((id) => !knownExamTypeIdsRef.current.has(id));
    if (newIds.length === 0) return;

    newIds.forEach((id) => knownExamTypeIdsRef.current.add(id));
    setCheckedExamTypeIds((prev) => new Set([...prev, ...newIds]));
  }, [sortableCards, getExamTypeId]);

  useEffect(() => {
    const newIds = sortableCards
      .map(getDateGroupKey)
      .filter((id) => !knownExamDateIdsRef.current.has(id));
    if (newIds.length === 0) return;

    newIds.forEach((id) => knownExamDateIdsRef.current.add(id));
    setCheckedExamDateIds((prev) => new Set([...prev, ...newIds]));
  }, [sortableCards, getDateGroupKey]);

  useEffect(() => {
    const newIds = sortableCards
      .map(getExamEyeId)
      .filter((id) => !knownExamEyeIdsRef.current.has(id));
    if (newIds.length === 0) return;

    newIds.forEach((id) => knownExamEyeIdsRef.current.add(id));
    setCheckedExamEyeIds((prev) => new Set([...prev, ...newIds]));
  }, [sortableCards, getExamEyeId]);

  const toggleExamTypeId = useCallback((id: string) => {
    setCheckedExamTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAllExamTypeIds = useCallback((checked: boolean) => {
    setCheckedExamTypeIds(
      checked ? new Set(knownExamTypeIdsRef.current) : new Set()
    );
  }, []);

  const toggleExamDateId = useCallback((id: string) => {
    setCheckedExamDateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAllExamDateIds = useCallback((checked: boolean) => {
    setCheckedExamDateIds(
      checked ? new Set(knownExamDateIdsRef.current) : new Set()
    );
  }, []);

  const toggleExamEyeId = useCallback((id: string) => {
    setCheckedExamEyeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAllExamEyeIds = useCallback((checked: boolean) => {
    setCheckedExamEyeIds(
      checked ? new Set(knownExamEyeIdsRef.current) : new Set()
    );
  }, []);

  // Cards actually shown below -- all filters must pass (AND, not OR).
  const filteredCards = useMemo(
    () =>
      sortableCards.filter(
        (exam) =>
          checkedExamTypeIds.has(getExamTypeId(exam)) &&
          checkedExamDateIds.has(getDateGroupKey(exam)) &&
          checkedExamEyeIds.has(getExamEyeId(exam))
      ),
    [
      sortableCards,
      checkedExamTypeIds,
      checkedExamDateIds,
      checkedExamEyeIds,
      getExamTypeId,
      getDateGroupKey,
      getExamEyeId,
    ]
  );

  // Group cards by exam date, preserving the existing (date desc) sort order
  const dateGroups: Array<[string, Array<ExternalExamType>]> = useMemo(() => {
    const groups = new Map<string, Array<ExternalExamType>>();

    filteredCards.forEach((exam) => {
      const dateKey = getDateGroupKey(exam);
      groups.set(dateKey, [...(groups.get(dateKey) ?? []), exam]);
    });

    return Array.from(groups.entries());
  }, [filteredCards, getDateGroupKey]);

  // "Exam Type" filter section
  const examTypeItems = useMemo(() => {
    const counts = new Map<string, number>();

    sortableCards.forEach((exam) => {
      const id = getExamTypeId(exam);
      counts.set(id, (counts.get(id) ?? 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([id, count]) => ({
        id,
        label: getExamTypeLabel(id),
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sortableCards, getExamTypeId, getExamTypeLabel]);

  // "Exam Date" filter section
  const examDateItems = useMemo(() => {
    const counts = new Map<string, number>();

    sortableCards.forEach((exam) => {
      const id = getDateGroupKey(exam);
      counts.set(id, (counts.get(id) ?? 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([id, count]) => ({
        id,
        label: getFormattedDisplayDate(id, languageCode),
        count,
      }))
      .sort((a, b) => b.id.localeCompare(a.id));
  }, [sortableCards, getDateGroupKey, languageCode]);

  // "Eye Type" filter section
  const examEyeItems = useMemo(() => {
    const counts = new Map<string, number>();

    sortableCards.forEach((exam) => {
      const id = getExamEyeId(exam);
      counts.set(id, (counts.get(id) ?? 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([id, count]) => ({
        id,
        label: id,
        count,
      }))
      .sort(
        (a, b) => (EYE_SORT_ORDER[a.id] ?? 0) - (EYE_SORT_ORDER[b.id] ?? 0)
      );
  }, [sortableCards, getExamEyeId]);

  const examTypeFilter = useMemo(
    () => ({
      items: examTypeItems,
      checkedIds: checkedExamTypeIds,
      onToggleItem: toggleExamTypeId,
      onToggleAll: toggleAllExamTypeIds,
    }),
    [examTypeItems, checkedExamTypeIds, toggleExamTypeId, toggleAllExamTypeIds]
  );

  const examDateFilter = useMemo(
    () => ({
      items: examDateItems,
      checkedIds: checkedExamDateIds,
      onToggleItem: toggleExamDateId,
      onToggleAll: toggleAllExamDateIds,
    }),
    [examDateItems, checkedExamDateIds, toggleExamDateId, toggleAllExamDateIds]
  );

  const examEyeFilter = useMemo(
    () => ({
      items: examEyeItems,
      checkedIds: checkedExamEyeIds,
      onToggleItem: toggleExamEyeId,
      onToggleAll: toggleAllExamEyeIds,
    }),
    [examEyeItems, checkedExamEyeIds, toggleExamEyeId, toggleAllExamEyeIds]
  );

  // Reset the provider values when the modal is closed/reopened
  useEffect(() => {
    if (!openCompareModal) return;

    setSortBy("laterality");
    setSortDirection("desc");
    setHasManualOrder(false);
    knownExamTypeIdsRef.current = new Set();
    knownExamDateIdsRef.current = new Set();
    knownExamEyeIdsRef.current = new Set();
    setCheckedExamTypeIds(new Set());
    setCheckedExamDateIds(new Set());
    setCheckedExamEyeIds(new Set());
  }, [openCompareModal]);

  return (
    <ExternalExamsContext.Provider
      value={{
        selectedPatient,
        openCompareModal,
        openDeleteModal,
        selectedExams,
        vfExternalExamsData,
        opdExternalExamsData,
        octExternalExamsData,
        isLoading,
        hasExternalExams,
        hasNoExternalExams,
        uniqueId,
        formattedPatientName,
        languageCode,
        sortOptions,
        effectiveSortBy,
        sortDirection,
        sortableCards,
        filteredCards,
        dateGroups,
        examTypeFilter,
        examDateFilter,
        examEyeFilter,
        actions: {
          setOpenCompareModal,
          setOpenDeleteModal,
          setSelectedExams,
          deleteExternalExam,
          handleSortByClick,
          reorderCards,
          resetManualOrder,
        },
      }}
    >
      {children}
    </ExternalExamsContext.Provider>
  );
};
